import { useCallback, useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { type Case, Role } from "@/briefpoint-client";
import Loading from "@/components/Loading";
import SearchInput from "@/components/SearchInput/SearchInput";
import { useCaseApi, useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useFirm from "@/hooks/useFirm";
import "./styles.scss";
import AddEditCaseModal from "@/components/CaseManagement/AddEditCaseModal";
import CaseTableMulti from "@/components/CaseManagement/CaseTableMulti";
import PageHeader from "@/components/PageHeader";
import type { CaseResponse, UserResponse } from "@/lib/types";

export default function Cases() {
  const { user, firm } = useAuth();
  if (!firm) throw new Error("Firm is undefined");
  const caseApi = useCaseApi();
  const tagApi = useTagApi();
  const [users, , , ,] = useFirm(firm?.id, false);
  const [cases, setCases] = useState<CaseResponse[] | undefined>();
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const { jurisdictions } = useAuth();
  const [showAddCase, setShowAddCase] = useState(false);

  async function AddCase(_case: Case) {
    if (!user?.firmId) throw new Error("User firmId is undefined");
    const newCase = (await caseApi.casePost({
      firmId: user.firmId,
      _case,
    })) as CaseResponse;
    if (newCase) {
      if (cases) {
        setCases((c) => [...(c ?? []), newCase]);
      } else {
        setCases([newCase]);
      }
    }
    closeAddCase();
  }

  function closeAddCase() {
    setShowAddCase(false);
  }

  const loadCases = useCallback(
    async (user: UserResponse | undefined) => {
      if (user?.firmId) {
        const cases = (await caseApi.caseGetAll_1({
          firmId: user.firmId,
          usersCasesOnly: !user?.role?.includes(Role.FirmAdmin),
        })) as CaseResponse[];
        setCases(cases);
      }
    },
    [caseApi],
  );

  useEffect(() => {
    loadCases(user);
  }, [user, loadCases]);

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();

      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setTags(tagsList);
    }
    if (!tags) {
      loadTags();
    }
  }, [tagApi, tags]);

  function getVenue(_case: CaseResponse) {
    const j = jurisdictions?.find((x) => x.id === _case.jurisdiction);
    return (
      j?.venues
        ?.find((x) => x.id === _case.venue)
        ?.shortName?.toLocaleLowerCase() ?? ""
    );
  }

  const filteredCases = cases?.filter((x) => {
    if (!search) return true;

    const title = x.title?.toLocaleLowerCase() ?? "";
    if (title.includes(search.toLowerCase())) {
      return true;
    }
    const shortTitle = x.shortTitle?.toLocaleLowerCase() ?? "";
    if (shortTitle.includes(search.toLowerCase())) {
      return true;
    }
    const caseNum = x.caseNumber?.toLocaleLowerCase() ?? "";
    if (caseNum.includes(search.toLowerCase())) {
      return true;
    }
    const venue = getVenue(x);
    if (venue.includes(search.toLowerCase())) {
      return true;
    }

    return false;
  });

  // Determines if we should show the "All Cases" list
  const shouldShowAllCases = (filteredCases?.length ?? 0) > 10;

  return (
    <>
      <Container className="page-container-multi">
        <Loading
          isLoading={
            cases === undefined ||
            jurisdictions === undefined ||
            users === undefined
          }
          className="mt-3"
        >
          <PageHeader
            testId="cases-header"
            title="Cases"
            cta={
              <Button
                data-testid="add-new-case-button"
                variant="primary"
                onClick={() => {
                  setShowAddCase(true);
                }}
              >
                Add New Case
              </Button>
            }
          />
          <div className="search-input-wrapper">
            <SearchInput
              value={search}
              placeholder="Search cases"
              onChange={(value) => setSearch(value.toLowerCase())}
              className="mb-2"
            />
          </div>
          {/* If there are no results, show a message */}
          {(filteredCases?.length ?? 0) <= 0 && !!search?.length ? (
            <div>No results found.</div>
          ) : (
            // If there are results, show the recent cases list
            <div>
              {/* Display the recent cases header to separate the recent cases from the all cases list when it shows up */}
              {shouldShowAllCases && (
                <h2 style={{ margin: "0px", marginBottom: "8px" }}>
                  Recent Cases
                </h2>
              )}
              <CaseTableMulti
                cases={filteredCases
                  ?.slice()
                  .sort((a, b) => {
                    if (a.lastActivity && b.lastActivity) {
                      return a.lastActivity > b.lastActivity ? -1 : 1;
                    }
                    if (a.lastActivity) {
                      return -1;
                    }
                    if (b.lastActivity) {
                      return 1;
                    }
                    return 0;
                  })
                  .slice(0, 10)}
                addCase={() => setShowAddCase(true)}
                users={users}
                jurisdictions={jurisdictions}
                showIfEmpty={true}
              />
            </div>
          )}
          {/* Display the all cases list if theres more than 10 cases */}
          {shouldShowAllCases && (
            <>
              <h2 style={{ marginTop: "15px", marginBottom: "8px" }}>
                All Cases
              </h2>
              <CaseTableMulti
                cases={filteredCases?.slice().sort((a, b) => {
                  if (a.shortTitle && b.shortTitle) {
                    return a.shortTitle.localeCompare(b.shortTitle);
                  }
                  if (a.shortTitle) {
                    return -1;
                  }
                  if (b.shortTitle) {
                    return 1;
                  }
                  return 0;
                })}
                addCase={() => {}}
                users={users}
                jurisdictions={jurisdictions}
                showIfEmpty={false}
              />
            </>
          )}
        </Loading>
      </Container>

      <AddEditCaseModal
        availableTags={tags}
        setAvailableTags={setTags}
        title="Add Case"
        confirm="Add"
        isShow={showAddCase}
        onClose={() => setShowAddCase(false)}
        onConfirm={AddCase}
        users={users}
        jurisdictions={jurisdictions ?? []}
      />
    </>
  );
}
